import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Input,
  List,
  ListItem,
  ListItemButton,
  ListItemContent,
  ListItemDecorator,
  ListSubheader,
  Modal,
  ModalDialog,
  Skeleton,
  Typography,
} from '@mui/joy';
import type { ColorPaletteProp } from '@mui/joy/styles';
import {
  Briefcase,
  ChatCircle,
  CheckSquare,
  ClockCounterClockwise,
  FileText,
  Hammer,
  MagnifyingGlass,
  User,
  X,
} from '@phosphor-icons/react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useDebounce } from '../../hooks/useDebounce';
import {
  MAX_SEARCH_QUERY_LENGTH,
  SEARCH_CATEGORIES,
  addRecentSearch,
  clearRecentSearches,
  fetchGlobalSearch,
  readRecentSearches,
  recentSearchesKey,
  searchResultHref,
} from '../../lib/globalSearch';
import type {
  SearchCategory,
  SearchFilterTab,
  SearchResultItem,
  SearchSortMode,
} from '../../types/search';

const CATEGORY_META: Record<SearchCategory, { label: string; icon: React.ElementType }> = {
  contacts: { label: 'Contacts', icon: User },
  opportunities: { label: 'Opportunities', icon: Briefcase },
  jobs: { label: 'Jobs', icon: Hammer },
  proposals: { label: 'Proposals', icon: FileText },
  invoices: { label: 'Invoices', icon: FileText },
  tasks: { label: 'Tasks', icon: CheckSquare },
  messages: { label: 'Messages', icon: ChatCircle },
};

/** Backend tone names → Joy palette. Unknown tones fall back to neutral. */
const TONE_COLORS: Record<string, ColorPaletteProp> = {
  green: 'success',
  blue: 'primary',
  sky: 'primary',
  purple: 'primary',
  teal: 'primary',
  amber: 'warning',
  orange: 'warning',
  red: 'danger',
};

const SEARCH_DEBOUNCE_MS = 250;

/** Pull the backend's `{ error }` / `{ detail }` message out of an api.ts error. */
function errorMessage(error: unknown): string {
  const data = (error as { responseData?: { error?: unknown; detail?: unknown } } | null)?.responseData;
  const message = data?.error ?? data?.detail;
  return typeof message === 'string' && message.trim()
    ? message
    : 'Search is unavailable right now. Please try again.';
}

interface GlobalSearchDialogProps {
  open: boolean;
  onClose: () => void;
}

export const GlobalSearchDialog: React.FC<GlobalSearchDialogProps> = ({ open, onClose }) => (
  <Modal open={open} onClose={onClose}>
    <ModalDialog
      aria-label="Global search"
      layout="center"
      sx={{
        width: { xs: 'calc(100vw - 32px)', sm: 640 },
        maxWidth: 640,
        top: { xs: '50%', sm: '12vh' },
        transform: { xs: 'translate(-50%, -50%)', sm: 'translateX(-50%)' },
        maxHeight: { xs: 'calc(100dvh - 32px)', sm: '76vh' },
        p: 0,
        gap: 0,
        overflow: 'hidden',
      }}
    >
      {/* The Modal unmounts this while closed, so every open starts fresh like a command palette. */}
      <SearchPanel onClose={onClose} />
    </ModalDialog>
  </Modal>
);

const SearchPanel: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const historyKey = recentSearchesKey(user);

  const [query, setQuery] = useState('');
  const [category, setCategory] = useState<SearchFilterTab>('all');
  const [sort, setSort] = useState<SearchSortMode>('relevance');
  const [recentSearches, setRecentSearches] = useState(() => readRecentSearches(historyKey));
  const listRef = useRef<HTMLUListElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // Filters and recents are clicked, but ↑↓/↵ live on the input — hand focus back.
  const refocus = () => inputRef.current?.focus();

  const debouncedQuery = useDebounce(query.trim(), SEARCH_DEBOUNCE_MS);
  const hasQuery = debouncedQuery.length > 0;

  const { data, isFetching, isError, error, refetch } = useQuery({
    queryKey: ['global-search', debouncedQuery, category, sort],
    queryFn: () => fetchGlobalSearch({ q: debouncedQuery, category, sort }),
    enabled: hasQuery,
    // Keep the last results on screen while the next keystroke loads.
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    retry: false,
  });

  const results = hasQuery ? data : undefined;
  const isSettling = query.trim() !== debouncedQuery || (isFetching && !results);

  // One flat list drives arrow-key navigation; sections render from the same rows.
  const sections = useMemo(() => {
    if (!results) return [];
    const rows: { key: string; title: string; items: SearchResultItem[] }[] = [];
    const best = category === 'all' ? results.best_match : null;
    if (best) rows.push({ key: 'best', title: 'Best match', items: [best] });
    const categories = category === 'all' ? SEARCH_CATEGORIES : [category];
    for (const cat of categories) {
      const items = (results.results[cat] ?? []).filter(
        (item) => !best || item.category !== best.category || item.id !== best.id,
      );
      if (items.length) rows.push({ key: cat, title: CATEGORY_META[cat].label, items });
    }
    return rows;
  }, [results, category]);

  const flatItems = useMemo(() => sections.flatMap((s) => s.items), [sections]);

  // The highlight belongs to one result set; a new query, tab or sort starts back at the top.
  const resultSetKey = `${debouncedQuery}|${category}|${sort}`;
  const [highlight, setHighlight] = useState({ key: resultSetKey, index: 0 });
  const lastIndex = Math.max(flatItems.length - 1, 0);
  const indexIn = (h: typeof highlight) => (h.key === resultSetKey ? Math.min(h.index, lastIndex) : 0);
  const activeIndex = indexIn(highlight);
  // Functional update: two key presses inside one render must both count.
  const setActiveIndex = (update: (index: number) => number) =>
    setHighlight((prev) => ({ key: resultSetKey, index: update(indexIn(prev)) }));

  // Browsers fire mousemove when rows shift under a resting cursor; only a real
  // pointer move may take the highlight from the keyboard.
  const lastPointer = useRef<{ x: number; y: number } | null>(null);
  const hoverRow = (index: number) => (event: React.MouseEvent) => {
    const { clientX: x, clientY: y } = event;
    const moved = !lastPointer.current || lastPointer.current.x !== x || lastPointer.current.y !== y;
    lastPointer.current = { x, y };
    if (moved) setActiveIndex(() => index);
  };

  useEffect(() => {
    listRef.current
      ?.querySelector(`[data-result-index="${activeIndex}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  const openItem = (item: SearchResultItem) => {
    if (query.trim()) setRecentSearches(addRecentSearch(historyKey, query));
    onClose();
    navigate(searchResultHref(item));
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (!flatItems.length) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((i) => (i + 1) % flatItems.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((i) => (i - 1 + flatItems.length) % flatItems.length);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const item = flatItems[activeIndex];
      if (item) openItem(item);
    }
  };

  const otherCategoriesWithHits =
    results && category !== 'all'
      ? SEARCH_CATEGORIES.filter((cat) => cat !== category && results.counts[cat] > 0)
      : [];

  let flatIndex = -1;

  return (
    <>
      <Box sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Input
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value.slice(0, MAX_SEARCH_QUERY_LENGTH))}
          onKeyDown={handleKeyDown}
          placeholder="Search contacts, opportunities, jobs, proposals…"
          variant="plain"
          size="lg"
          startDecorator={<MagnifyingGlass size={20} />}
          endDecorator={
            query ? (
              <IconButton size="sm" variant="plain" aria-label="Clear search" onClick={() => setQuery('')}>
                <X size={16} />
              </IconButton>
            ) : (
              <Chip size="sm" variant="outlined" sx={{ display: { xs: 'none', sm: 'inline-flex' } }}>
                Esc
              </Chip>
            )
          }
          slotProps={{
            input: {
              ref: inputRef,
              'aria-label': 'Search',
              role: 'combobox',
              'aria-expanded': flatItems.length > 0,
              'aria-controls': 'global-search-results',
              'aria-activedescendant': flatItems.length ? `global-search-result-${activeIndex}` : undefined,
            },
          }}
          sx={{ '--Input-focusedThickness': '0px', boxShadow: 'none' }}
        />
      </Box>

      {hasQuery && (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            columnGap: 1,
            px: 1.5,
            pb: { xs: 0.5, sm: 0 },
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box role="tablist" aria-label="Filter by type" sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, py: 1, flex: { xs: '1 1 100%', sm: 1 }, minWidth: 0 }}>
            {(['all', ...SEARCH_CATEGORIES] as SearchFilterTab[]).map((tab) => {
              const selected = category === tab;
              return (
                <Button
                  key={tab}
                  role="tab"
                  aria-selected={selected}
                  size="sm"
                  variant={selected ? 'solid' : 'plain'}
                  color={selected ? 'primary' : 'neutral'}
                  onClick={() => {
                    setCategory(tab);
                    refocus();
                  }}
                  sx={{ borderRadius: 'xl', minHeight: 28, px: 1.25, fontWeight: selected ? 600 : 500 }}
                >
                  {tab === 'all' ? 'All' : CATEGORY_META[tab].label}
                  {results && (
                    <Typography
                      component="span"
                      level="body-xs"
                      sx={{ ml: 0.75, color: selected ? 'inherit' : 'text.tertiary', opacity: selected ? 0.85 : 1 }}
                    >
                      {results.counts[tab]}
                    </Typography>
                  )}
                </Button>
              );
            })}
          </Box>
          <Button
            size="sm"
            variant="plain"
            color="neutral"
            onClick={() => {
              setSort((s) => (s === 'relevance' ? 'recent' : 'relevance'));
              refocus();
            }}
            sx={{ flexShrink: 0, whiteSpace: 'nowrap', ml: 'auto' }}
          >
            Sort: {sort === 'relevance' ? 'Best match' : 'Most recent'}
          </Button>
        </Box>
      )}

      <Box sx={{ overflowY: 'auto', flex: 1, minHeight: 0 }}>
        {!hasQuery && !query.trim() && (
          <RecentSearches
            items={recentSearches}
            onSelect={(recent) => {
              setQuery(recent);
              refocus();
            }}
            onClear={() => {
              clearRecentSearches(historyKey);
              setRecentSearches([]);
              refocus();
            }}
          />
        )}

        {(query.trim() || hasQuery) && isError && !isSettling && (
          <Box sx={{ p: 4, textAlign: 'center' }} role="alert">
            <Typography level="title-sm">{errorMessage(error)}</Typography>
            <Button size="sm" variant="soft" sx={{ mt: 1.5 }} onClick={() => void refetch()}>
              Retry
            </Button>
          </Box>
        )}

        {(query.trim() || hasQuery) && !isError && !results && isSettling && <ResultSkeleton />}

        {results && !isError && flatItems.length === 0 && !isSettling && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography level="title-sm">No matches for “{debouncedQuery}”</Typography>
            <Typography level="body-sm" sx={{ mt: 0.5, color: 'text.tertiary' }}>
              Try a name, address, phone, email, or a record number like #1042 or job 42.
            </Typography>
          </Box>
        )}

        {flatItems.length > 0 && !isError && (
          <List
            ref={listRef}
            id="global-search-results"
            role="listbox"
            aria-label="Search results"
            size="sm"
            sx={{
              py: 1,
              opacity: isFetching ? 0.6 : 1,
              transition: 'opacity 120ms ease',
              '--ListItem-paddingX': '12px',
              '--ListItemDecorator-size': '40px',
            }}
          >
            {sections.map((section) => (
              <ListItem nested key={section.key} role="presentation">
                <ListSubheader sticky sx={{ px: 1.5 }}>
                  {section.title}
                  {section.key !== 'best' && results && (
                    <Typography component="span" level="body-xs" sx={{ ml: 0.75, color: 'text.tertiary' }}>
                      {results.counts[section.key as SearchCategory]}
                    </Typography>
                  )}
                </ListSubheader>
                <List role="presentation" sx={{ '--List-gap': '2px', px: 0.75 }}>
                  {section.items.map((item) => {
                    flatIndex += 1;
                    const index = flatIndex;
                    return (
                      <ResultRow
                        key={`${section.key}-${item.category}-${item.id}`}
                        item={item}
                        index={index}
                        active={index === activeIndex}
                        onHover={hoverRow(index)}
                        onSelect={() => openItem(item)}
                      />
                    );
                  })}
                </List>
              </ListItem>
            ))}
          </List>
        )}

        {otherCategoriesWithHits.length > 0 && !isError && (
          <Box sx={{ px: 2, py: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
            <Typography level="body-xs" sx={{ color: 'text.tertiary', mb: 0.75 }}>
              “{debouncedQuery}” also appears in
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {otherCategoriesWithHits.map((cat) => (
                <Chip
                  key={cat}
                  size="sm"
                  variant="outlined"
                  onClick={() => {
                    setCategory(cat);
                    refocus();
                  }}
                >
                  {CATEGORY_META[cat].label} · {results?.counts[cat]}
                </Chip>
              ))}
            </Box>
          </Box>
        )}
      </Box>

      <Box
        sx={{
          display: { xs: 'none', sm: 'flex' },
          gap: 2,
          px: 2,
          py: 1,
          borderTop: '1px solid',
          borderColor: 'divider',
          color: 'text.tertiary',
        }}
      >
        <Typography level="body-xs">↑↓ to navigate</Typography>
        <Typography level="body-xs">↵ to open</Typography>
        <Typography level="body-xs">esc to close</Typography>
      </Box>
    </>
  );
};

const ResultRow: React.FC<{
  item: SearchResultItem;
  index: number;
  active: boolean;
  onHover: (event: React.MouseEvent) => void;
  onSelect: () => void;
}> = ({ item, index, active, onHover, onSelect }) => {
  const Icon = CATEGORY_META[item.category]?.icon ?? MagnifyingGlass;
  const tone = (item.stage_tone && TONE_COLORS[item.stage_tone]) || 'neutral';
  return (
    <ListItem role="presentation">
      <ListItemButton
        id={`global-search-result-${index}`}
        data-result-index={index}
        role="option"
        aria-selected={active}
        selected={active}
        color="neutral"
        variant={active ? 'soft' : 'plain'}
        onMouseMove={onHover}
        onClick={onSelect}
        sx={{
          borderRadius: 'sm',
          py: 1,
          alignItems: 'center',
          // The highlight follows real pointer moves, so a resting cursor must not paint a second one.
          '&:not(.Mui-selected):hover': { bgcolor: 'transparent' },
          '&.Mui-selected': { boxShadow: 'inset 3px 0 0 var(--joy-palette-primary-500)' },
        }}
      >
        <ListItemDecorator>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 'sm',
              display: 'grid',
              placeItems: 'center',
              bgcolor: 'background.level1',
              color: 'text.secondary',
            }}
          >
            <Icon size={18} />
          </Box>
        </ListItemDecorator>
        <ListItemContent sx={{ minWidth: 0 }}>
          <Typography level="title-sm" noWrap>
            {item.title}
          </Typography>
          {item.subtitle && (
            <Typography level="body-xs" noWrap sx={{ color: 'text.tertiary' }}>
              {item.subtitle}
            </Typography>
          )}
        </ListItemContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0, ml: 1 }}>
          {item.stage_name && (
            <Chip size="sm" variant="soft" color={tone} sx={{ display: { xs: 'none', sm: 'inline-flex' } }}>
              {item.stage_name}
            </Chip>
          )}
          <Typography level="body-xs" sx={{ color: 'text.tertiary', minWidth: 56, textAlign: 'right' }}>
            {item.entity_label}
          </Typography>
        </Box>
      </ListItemButton>
    </ListItem>
  );
};

const RecentSearches: React.FC<{
  items: string[];
  onSelect: (query: string) => void;
  onClear: () => void;
}> = ({ items, onSelect, onClear }) => {
  if (!items.length) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography level="title-sm">Search everything in one place</Typography>
        <Typography level="body-sm" sx={{ mt: 0.5, color: 'text.tertiary' }}>
          Find contacts, opportunities, jobs, proposals, tasks and messages by name, address, phone,
          email, or record number.
        </Typography>
      </Box>
    );
  }
  return (
    <List size="sm" sx={{ py: 1, '--ListItem-paddingX': '12px' }}>
      <ListItem
        endAction={
          <Button size="sm" variant="plain" color="neutral" onClick={onClear}>
            Clear
          </Button>
        }
      >
        <Typography level="body-xs" sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.tertiary' }}>
          Recent searches
        </Typography>
      </ListItem>
      {items.map((recent) => (
        <ListItem key={recent} sx={{ px: 0.75 }}>
          <ListItemButton onClick={() => onSelect(recent)} sx={{ borderRadius: 'sm' }}>
            <ListItemDecorator>
              <ClockCounterClockwise size={16} />
            </ListItemDecorator>
            {recent}
          </ListItemButton>
        </ListItem>
      ))}
    </List>
  );
};

const ResultSkeleton: React.FC = () => (
  <Box sx={{ p: 1.5, display: 'grid', gap: 1.5 }} aria-busy="true" aria-label="Loading results">
    {[0, 1, 2, 3].map((row) => (
      <Box key={row} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Skeleton variant="rectangular" width={32} height={32} sx={{ borderRadius: 'sm' }} />
        <Box sx={{ flex: 1 }}>
          <Skeleton variant="text" level="title-sm" width={`${60 - row * 8}%`} />
          <Skeleton variant="text" level="body-xs" width={`${40 - row * 4}%`} />
        </Box>
      </Box>
    ))}
  </Box>
);
